import StyledNextPreviousButtons from '@/components/styled/StyledNextPreviousButtons';
import StyledSelect from '@/components/styled/StyledSelect';

import StyledTable from '@/components/styled/StyledTable';
import Loader from '@/components/ui/Loader';
import { LOCALES_MAP } from '@/constants/localeMap';
import { PAGE_SIZE } from '@/constants/pageSize';
import { PATHS } from '@/constants/Paths';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  useGetAllReservationsSummary,
  useGetConfirmedReservationsSummary,
  useGetDistributorReservationsWithStats,
  useGetRequestsReservationsSummary,
} from '@/hooks/useStats';
import useTableControls from '@/hooks/useTableControls';
import { useTranslation } from '@/i18n';
import { EstimateStatus } from '@/types/estimates/EstimateStatus';
import { LabelValue } from '@/types/global';

import {
  getReservationsSummaryColumns,
  ReservationSummary,
} from '@/types/stats/ReservationSummaryColumnDef';
import { SortingState } from '@tanstack/react-table';
import { eachMonthOfInterval, format } from 'date-fns';
import React, { useMemo } from 'react';
import { useDistributorLabels } from '@/hooks/useDistributorLabels';
import { Control, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';

type ReservationFilters = {
  receiver?: LabelValue<string>;
  agency?: LabelValue<string>;
  agent?: LabelValue<string>;
  client?: LabelValue<string>;
  confirmationMonth?: LabelValue<string>;
  reservationRequestMonth?: LabelValue<string>;
  creationMonth?: LabelValue<string>;
};

interface ReservationTableProps {
  reservations: ReservationSummary[];
  total: number;
  agenciesOptions?: LabelValue<string>[];
  agentsOptions: LabelValue<string>[];
  clientsOptions: LabelValue<string>[];
  receiverOptions?: LabelValue<string>[];
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  sorting: SortingState;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  control: Control<ReservationFilters>;
  shouldDisplayConfirmationDateFilter?: boolean;
  shouldDisplayCreatedAtFilter?: boolean;
  displayReceiver?: boolean;
  displayDistributor?: boolean;
  shouldDefineRowClickAction?: boolean;
  isDistributor?: boolean;
  shouldDisplayAmountWithoutMarginRate?: boolean;
}

const ReservationTable = ({
  reservations,
  total,
  page,
  setPage,
  sorting,
  setSorting,
  receiverOptions,
  agenciesOptions,
  agentsOptions,
  clientsOptions,
  control,
  shouldDisplayConfirmationDateFilter = true,
  shouldDisplayCreatedAtFilter = false,
  displayReceiver = false,
  displayDistributor = false,
  shouldDefineRowClickAction = true,
  shouldDisplayAmountWithoutMarginRate = false,
  isDistributor = false,
}: ReservationTableProps) => {
  const { t } = useTranslation();
  const { isTourOperator } = useDistributorLabels();
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();

  const useAgencyColumnLabel = isDistributor && isTourOperator;

  const summaryColumns = useMemo(
    () => getReservationsSummaryColumns(useAgencyColumnLabel),
    [useAgencyColumnLabel, currentLanguage],
  );

  const monthOptions = useMemo(
    () =>
      eachMonthOfInterval({ start: new Date(2025, 0, 1), end: new Date() }).map(
        (month) => ({
          label: format(month, 'LLLL yyyy', {
            locale: LOCALES_MAP[currentLanguage],
          }),
          value: format(month, 'yyyy-MM'),
        }),
      ),
    [currentLanguage],
  );

  return (
    <>
      <div className="flex flex-col gap-2">
        <span className="text-xs font-normal text-gray-600">
          {t('reservations.filterBy')}
        </span>
        <div className="flex flex-col md:flex-row gap-4 w-fit">
          {displayReceiver && receiverOptions && (
            <StyledSelect
              className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
              control={control}
              options={receiverOptions}
              name="receiver"
              placeholder={t('reservations.receiver')}
              isClearable={true}
            />
          )}
          {displayDistributor && agenciesOptions && (
            <StyledSelect
              className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
              control={control}
              options={agenciesOptions}
              name="agency"
              placeholder={t('reservations.distributor')}
              isClearable={true}
            />
          )}
          <StyledSelect
            className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
            control={control}
            options={agentsOptions}
            name="agent"
            isClearable={true}
            placeholder={t('reservations.agent')}
          />
          <StyledSelect
            className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
            control={control}
            options={clientsOptions}
            name="client"
            isClearable={true}
            placeholder={
              useAgencyColumnLabel
                ? t('distributorAgencies.columns.agency')
                : t('reservations.client')
            }
          />
          {shouldDisplayConfirmationDateFilter && (
            <StyledSelect
              className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
              control={control}
              options={monthOptions}
              name="confirmationMonth"
              placeholder={t('reservations.confirmationMonth')}
              isClearable={true}
            />
          )}
          {!shouldDisplayConfirmationDateFilter &&
            !shouldDisplayCreatedAtFilter && (
              <StyledSelect
                className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                control={control}
                options={monthOptions}
                name="reservationRequestMonth"
                placeholder={t('reservations.reservationRequestMonth')}
                isClearable={true}
              />
            )}
          {shouldDisplayCreatedAtFilter && (
            <StyledSelect
              className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
              control={control}
              options={monthOptions}
              name="creationMonth"
              placeholder={t('reservations.creationMonth')}
              isClearable={true}
            />
          )}
        </div>
      </div>
      <StyledTable
        columns={summaryColumns
          .filter((column) =>
            shouldDisplayAmountWithoutMarginRate
              ? true
              : column.header !== 'Montant hors marge',
          )
          .filter((column) =>
            shouldDisplayConfirmationDateFilter
              ? column.header !==
                t('reservations.columns.reservationRequestMonth')
              : column.header !== t('reservations.columns.confirmationDate'),
          )
          .filter((column) =>
            displayReceiver
              ? true
              : column.header !== t('reservations.columns.supplier'),
          )
          .filter((column) =>
            displayDistributor
              ? true
              : column.header !== t('reservations.columns.distributor'),
          )
          .filter((column) =>
            shouldDisplayCreatedAtFilter
              ? column.header !==
                t('reservations.columns.reservationRequestMonth')
              : column.header !== t('reservations.columns.creationDate'),
          )}
        data={reservations}
        getRowId={(row) => row.id}
        sorting={sorting}
        setSorting={setSorting}
        onRowClick={
          shouldDefineRowClickAction
            ? (row) => {
                if (isDistributor) {
                  navigate(
                    PATHS.distributors.estimates.view
                      .replace(':estimateId', row.original.id)
                      .replace(':projectId', row.original.project.id),
                  );
                } else
                  navigate(
                    PATHS.home.reservations.requests.view.replace(
                      ':estimateId',
                      row.original.id,
                    ),
                  );
              }
            : undefined
        }
      />
      <StyledNextPreviousButtons
        page={page}
        setPage={setPage}
        dataLength={total}
        pageSize={PAGE_SIZE}
        label={t('reservations.reservationRequest')}
        pluralLabel={t('reservations.reservationRequests')}
      />
    </>
  );
};

export const ConfirmedReservationsTable = () => {
  const { control, filters } = useReservationFilters();

  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();
  const {
    data: reservationsResponse,
    isLoading,
    isSuccess,
  } = useGetConfirmedReservationsSummary(
    {
      page,
      pageSize: PAGE_SIZE,
      sortField,
      sortOrder,
      filters,
    },
    (data) => ({
      ...data,
      agencies: data.agencies.map(({ id, name }) => ({
        label: name,
        value: id,
      })),
      agents: data.agents.map(({ id, firstname, lastname }) => ({
        label: `${firstname} ${lastname}`,
        value: id,
      })),
      clients: data.clients.map(({ id, mainContact }) => ({
        label: `${mainContact.firstName} ${mainContact.lastName}`,
        value: id,
      })),
    }),
  );

  return isLoading ? (
    <Loader />
  ) : (
    isSuccess && (
      <ReservationTable
        reservations={reservationsResponse.estimates}
        total={reservationsResponse.total}
        page={page}
        setPage={setPage}
        sorting={sorting}
        setSorting={setSorting}
        agenciesOptions={reservationsResponse.agencies}
        agentsOptions={reservationsResponse.agents}
        clientsOptions={reservationsResponse.clients}
        control={control}
        displayDistributor
      />
    )
  );
};

export const ReservationRequestsTable = () => {
  const { control, filters } = useReservationFilters();
  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();
  const {
    data: reservationsResponse,
    isLoading,
    isSuccess,
  } = useGetRequestsReservationsSummary(
    {
      page,
      pageSize: PAGE_SIZE,
      sortField,
      sortOrder,
      filters,
    },
    (data) => ({
      ...data,
      agencies: data.agencies.map(({ id, name }) => ({
        label: name,
        value: id,
      })),
      agents: data.agents.map(({ id, firstname, lastname }) => ({
        label: `${firstname} ${lastname}`,
        value: id,
      })),
      clients: data.clients.map(({ id, mainContact }) => ({
        label: `${mainContact.firstName} ${mainContact.lastName}`,
        value: id,
      })),
    }),
  );

  return isLoading ? (
    <Loader />
  ) : (
    isSuccess && (
      <ReservationTable
        reservations={reservationsResponse.estimates}
        total={reservationsResponse.total}
        page={page}
        setPage={setPage}
        sorting={sorting}
        setSorting={setSorting}
        agenciesOptions={reservationsResponse.agencies}
        agentsOptions={reservationsResponse.agents}
        clientsOptions={reservationsResponse.clients}
        control={control}
        shouldDisplayConfirmationDateFilter={false}
        displayDistributor
      />
    )
  );
};

export const AdminReservationsTable = ({
  status,
}: {
  status: EstimateStatus;
}) => {
  const { control, filters } = useReservationFilters();
  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();

  const {
    data: allReservationsResponse,
    isSuccess,
    isLoading,
  } = useGetAllReservationsSummary(
    {
      page: page,
      pageSize: PAGE_SIZE,
      sortField: sortField,
      sortOrder: sortOrder,
      filters,
      status,
    },
    (data) => ({
      ...data,
      agencies: data.agencies.map(({ id, name }) => ({
        label: name,
        value: id,
      })),
      agents: data.agents.map(({ id, firstname, lastname }) => ({
        label: `${firstname} ${lastname}`,
        value: id,
      })),
      clients: data.clients.map(({ id, mainContact }) => ({
        label: `${mainContact.firstName} ${mainContact.lastName}`,
        value: id,
      })),
      receivers: data.receivers.map(({ id, name }) => ({
        label: name,
        value: id,
      })),
    }),
  );

  return isLoading ? (
    <Loader />
  ) : (
    isSuccess && (
      <ReservationTable
        reservations={allReservationsResponse.estimates}
        total={allReservationsResponse.total}
        page={page}
        setPage={setPage}
        sorting={sorting}
        setSorting={setSorting}
        receiverOptions={allReservationsResponse.receivers}
        agenciesOptions={allReservationsResponse.agencies}
        agentsOptions={allReservationsResponse.agents}
        clientsOptions={allReservationsResponse.clients}
        control={control}
        shouldDisplayConfirmationDateFilter={status === EstimateStatus.RESERVED}
        shouldDefineRowClickAction={false}
        displayReceiver
        displayDistributor
      />
    )
  );
};

export const DistributorReservationsTable = ({
  status,
}: {
  status: EstimateStatus;
}) => {
  const { isTourOperator } = useDistributorLabels();
  const { control, filters } = useReservationFilters();
  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();

  const {
    data: distributorReservationsResponse,
    isSuccess,
    isLoading,
  } = useGetDistributorReservationsWithStats(
    {
      page: page,
      pageSize: PAGE_SIZE,
      sortField: sortField,
      sortOrder: sortOrder,
      filters,
      status,
    },
    (data) => ({
      ...data,
      agents: data.agents.map(({ id, firstname, lastname }) => ({
        label: `${firstname} ${lastname}`,
        value: id,
      })),
      clients: data.clients.map(({ id, mainContact, agencyName }) => ({
        label:
          isTourOperator && agencyName
            ? agencyName
            : `${mainContact.firstName} ${mainContact.lastName}`,
        value: id,
      })),
      receivers: data.receivers.map(({ id, name }) => ({
        label: name,
        value: id,
      })),
    }),
  );

  return isLoading ? (
    <Loader />
  ) : (
    isSuccess && (
      <ReservationTable
        reservations={distributorReservationsResponse.estimates}
        total={distributorReservationsResponse.total}
        page={page}
        setPage={setPage}
        sorting={sorting}
        setSorting={setSorting}
        receiverOptions={distributorReservationsResponse.receivers}
        agentsOptions={distributorReservationsResponse.agents}
        clientsOptions={distributorReservationsResponse.clients}
        control={control}
        shouldDisplayConfirmationDateFilter={status === EstimateStatus.RESERVED}
        shouldDisplayCreatedAtFilter={status === EstimateStatus.PENDING}
        displayReceiver
        shouldDisplayAmountWithoutMarginRate
        shouldDefineRowClickAction
        isDistributor
      />
    )
  );
};

const useReservationFilters = () => {
  const { control } = useForm<ReservationFilters>({
    mode: 'onChange',
    defaultValues: {
      receiver: undefined,
      agency: undefined,
      agent: undefined,
      client: undefined,
      confirmationMonth: undefined,
      reservationRequestMonth: undefined,
      creationMonth: undefined,
    },
  });

  const selectedReceiver = useWatch({ control, name: 'receiver' });
  const selectedAgency = useWatch({ control, name: 'agency' });
  const selectedAgent = useWatch({ control, name: 'agent' });
  const selectedClient = useWatch({ control, name: 'client' });
  const selectedConfirmationMonth = useWatch({
    control,
    name: 'confirmationMonth',
  });
  const selectedReservationRequestMonth = useWatch({
    control,
    name: 'reservationRequestMonth',
  });
  const selectedCreationMonth = useWatch({ control, name: 'creationMonth' });

  const filters = useMemo(
    () => ({
      companyId: selectedReceiver?.value,
      agencyId: selectedAgency?.value,
      agentId: selectedAgent?.value,
      clientId: selectedClient?.value,
      confirmationMonth: selectedConfirmationMonth?.value,
      reservationRequestMonth: selectedReservationRequestMonth?.value,
      creationMonth: selectedCreationMonth?.value,
    }),
    [
      selectedAgency?.value,
      selectedAgent?.value,
      selectedClient?.value,
      selectedConfirmationMonth?.value,
      selectedReceiver?.value,
      selectedReservationRequestMonth?.value,
      selectedCreationMonth?.value,
    ],
  );

  return { control, filters };
};
